import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon, Users, Clock, Sparkles, Home, MapPin, Coffee, Waves, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const BookingSection = () => {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('2');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    
    if (checkOut <= checkIn) {
      toast.error('Check-out date must be after check-in date');
      return;
    }
    
    if (!name || !email) {
      toast.error('Please fill in your contact information');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Guest count validation
    const guestCount = parseInt(guests, 10);
    if (isNaN(guestCount) || guestCount < 1 || guestCount > 6) {
      toast.error('Please enter a valid number of guests (1-6)');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit booking inquiry to backend API
      const response = await fetch(`${BACKEND_URL}/api/booking-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          check_in: format(checkIn, 'yyyy-MM-dd'),
          check_out: format(checkOut, 'yyyy-MM-dd'),
          guests: guestCount,
          name: name.trim(),
          email: email.trim().toLowerCase(),
          message: message.trim() || null
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit booking inquiry');
      }
      
      // Success - show message and reset form
      toast.success(data.message || 'Booking request submitted! We\'ll contact you shortly.');
      
      // Reset form
      setCheckIn(null);
      setCheckOut(null);
      setGuests('2');
      setName('');
      setEmail('');
      setMessage('');
      
    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error(error.message || 'Failed to submit booking request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="booking" className="py-16 sm:py-20 px-4 bg-muted/30">
      <div className="max-w-5xl mx-auto">
        {/* Hero Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">
            Your Cascais Adventure Awaits
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Experience modern Scandinavian living in the heart of Portugal's most charming coastal town. 
            Send us your dates and we'll craft the perfect stay for you.
          </p>
        </div>
        
        {/* Recommendation Banner */}
        <div className="mb-10 p-6 rounded-tl-xl rounded-br-xl rounded-tr-none rounded-bl-none bg-primary/10 border border-primary/20">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none bg-primary/20 text-primary">
              <Sparkles size={24} />
            </div>
            <div>
              <h3 className="text-lg font-display font-semibold text-foreground mb-2">
                Our Recommendation: Stay ~10 Days
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                To truly experience everything the Cascais region has to offer—from the fairy-tale palaces of Sintra 
                to the vibrant streets of Lisbon, the stunning beaches, and the legendary local cuisine—we recommend 
                a stay of approximately 10 days. This gives you the perfect balance of exploration and relaxation.
              </p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Information */}
          <div className="space-y-6">
            {/* Pricing Information */}
            <Card className="p-6 bg-card border-border">
              <h3 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
                <Clock size={20} className="text-primary" />
                Flexible Pricing
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Our pricing is tailored to the time of year and seasonal demand, remaining competitive 
                and in-line with comparable hotels and premium Airbnb/short-term rentals in the Cascais area. 
                When you submit your inquiry, we'll provide a personalized quote based on your specific dates.
              </p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none bg-muted/50">
                  <div className="font-medium text-foreground">Peak Season</div>
                  <div className="text-muted-foreground">June - September</div>
                </div>
                <div className="p-3 rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none bg-muted/50">
                  <div className="font-medium text-foreground">Shoulder Season</div>
                  <div className="text-muted-foreground">Apr-May, Oct-Nov</div>
                </div>
              </div>
            </Card>
            
            {/* Stay Length */}
            <Card className="p-6 bg-card border-border">
              <h3 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
                <Home size={20} className="text-primary" />
                Flexible Stay Length
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                There are no minimum or maximum stay limits within reason. Whether you're planning a long weekend 
                getaway or an extended month-long escape, Urban Oasis is your home away from home.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  <span>Weekend escapes welcome</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  <span>Extended stays available</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  <span>Remote work friendly with high-speed WiFi</span>
                </div>
              </div>
            </Card>
            
            {/* Services */}
            <Card className="p-6 bg-card border-border">
              <h3 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
                <Sparkles size={20} className="text-primary" />
                Additional Services
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                For longer stays, we're happy to arrange housekeeping, linen service, and other amenities upon request. 
                Just let us know what you need to make your stay perfect.
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none bg-muted/50 text-muted-foreground">Housekeeping</div>
                <div className="p-2 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none bg-muted/50 text-muted-foreground">Linen Service</div>
                <div className="p-2 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none bg-muted/50 text-muted-foreground">Airport Transfers</div>
                <div className="p-2 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none bg-muted/50 text-muted-foreground">Local Tips & Guides</div>
              </div>
            </Card>
            
            {/* Why Stay Here */}
            <Card className="p-6 bg-card border-border">
              <h3 className="text-xl font-display font-semibold text-foreground mb-4">
                Why Urban Oasis?
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none bg-primary/10 text-primary">
                    <MapPin size={16} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Prime Downtown Location</div>
                    <div className="text-xs text-muted-foreground">Steps from beaches, restaurants, and nightlife</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none bg-primary/10 text-primary">
                    <Coffee size={16} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Modern Comfort</div>
                    <div className="text-xs text-muted-foreground">Scandinavian design meets Portuguese charm</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none bg-primary/10 text-primary">
                    <Waves size={16} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Perfect Base Camp</div>
                    <div className="text-xs text-muted-foreground">Easy access to Sintra, Lisbon, and beyond</div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          
          {/* Right Column - Booking Form */}
          <div>
            <Card className="p-6 sm:p-8 bg-card border-border shadow-lg sticky top-24">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-6">
                Request Your Dates
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Dates Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="checkin" className="text-sm font-medium">Check-in Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal h-10 border-input hover:border-primary transition-all duration-300"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {checkIn ? format(checkIn, 'PP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={checkIn}
                          onSelect={setCheckIn}
                          initialFocus
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="checkout" className="text-sm font-medium">Check-out Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal h-10 border-input hover:border-primary transition-all duration-300"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {checkOut ? format(checkOut, 'PP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={checkOut}
                          onSelect={setCheckOut}
                          initialFocus
                          disabled={(date) => date < new Date() || (checkIn && date <= checkIn)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                
                {/* Number of Guests */}
                <div className="space-y-1.5">
                  <Label htmlFor="guests" className="text-sm font-medium">Number of Guests</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="guests"
                      type="number"
                      min="1"
                      max="6"
                      value={guests}
                      onChange={(e) => setGuests(e.target.value)}
                      className="pl-10 h-10 border-input focus:border-primary transition-all duration-300"
                      placeholder="2"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Maximum 6 guests</p>
                </div>
                
                {/* Contact Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-10 border-input focus:border-primary transition-all duration-300"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-10 border-input focus:border-primary transition-all duration-300"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>
                
                {/* Special Requests */}
                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-sm font-medium">Special Requests (Optional)</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="min-h-[100px] border-input focus:border-primary transition-all duration-300 resize-none"
                    placeholder="Let us know about housekeeping needs, airport transfers, or any questions..."
                  />
                </div>
                
                {/* Submit Button */}
                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-warm btn-transition py-5 disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Request Booking & Pricing'
                  )}
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  We'll respond with availability and a personalized quote within 24 hours
                </p>
              </form>
            </Card>
          </div>
        </div>
        
        {/* Bottom CTA */}
        <div className="mt-12 text-center p-8 rounded-tl-xl rounded-br-xl rounded-tr-none rounded-bl-none bg-card border border-border">
          <h3 className="text-2xl font-display font-semibold text-foreground mb-3">
            Questions Before Booking?
          </h3>
          <p className="text-muted-foreground mb-4">
            We're happy to answer any questions about the property, the area, or help you plan your perfect Portuguese adventure.
          </p>
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            Contact Us Directly
          </Button>
        </div>
      </div>
    </section>
  );
};